import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { LevelAsset } from '@services/assets-data.service';
import { Rule, RulesDataService } from '@services/rules-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { RuleEvent } from '@services/events-data.service';
import { CmmsService, ICaseFile, iTriggerTypes } from '@services/cmms.service';
import { IOrganisationsHierarchy } from '@services/organisation-data.service';
import { HttpErrorResponse } from '@angular/common/http';

/** createCMMSDialogData
 * data passed to the CreateCmmsCaseComponent dialog, 
 * includes the asset item, event (if attached to event), whether it is attached to an event, 
 * whether it is editing an existing case file, and the case file (if editing)
 */
export interface createCMMSDialogData {
	assetItem: LevelAsset;
	parent: IOrganisationsHierarchy;
	ruleEvent?: RuleEvent;
	isAttachedtoEvent: boolean;
	editing: boolean;
	creating: boolean
	caseFile: ICaseFile;
}

/** CreateCmmsCaseComponent
 * Dialog for creating a CMMS case, can be attached to an event or standalone, 
 * if attached to an event, the trigger type is determined by the event and cannot be changed, 
 * if standalone, the user must select a trigger type. If editing an existing case file, 
 * the form is populated with the case file data and trigger type cannot be changed. 
 */
@Component({
	selector: 'app-create-cmms-case',
	templateUrl: './create-cmms-case.component.html',
	styleUrl: './create-cmms-case.component.scss'
})
export class CreateCmmsCaseComponent {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected AccountUID */
	public selectedAccountUID: string;

	/** Selected Organisation ID */
	public selectedOrganisationUID: string;

	/** Form Group */
	public dialogForm: FormGroup;

	/** asset */
	public asset: LevelAsset;

	/** attachedToEvent */
	public attachedToEvent: boolean = true;

	/** creatingCase */
	public creatingCase: boolean = true;

	/** triggerTypeFields - used for display and value in trigger type dropdown, values correspond to properties in iTriggerTypes */
	public triggerTypeFields: Object = { text: "Description", value: "TriggerType" };

	/** triggerTypes - values correspond to value in db table */
	public triggerTypes: iTriggerTypes[] = []

	/** editing */
	public editing: boolean = false;

	/** case file */
	public caseFile: ICaseFile;

	/** ruleFields */
	public ruleFields: Object = { text: 'RuleDescription', value: 'RuleID' };

	/** Rules */
	public Rules: Rule[] = [];

	/** currentUser */
	public currentUser: WebAppUser;
	public parent: IOrganisationsHierarchy;

	/**
	 * constructor
	 * @param fb 
	 * @param dialogRef 
	 * @param globalFunctions 
	 * @param siteSubscriptions 
	 * @param rulesDataService 
	 * @param authenticationService 
	 * @param cmmsService 
	 * @param dialogData 
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<CreateCmmsCaseComponent>,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		private rulesDataService: RulesDataService,
		private authenticationService: AuthenticationService,
		private cmmsService: CmmsService,
		@Inject(MAT_DIALOG_DATA) public dialogData: createCMMSDialogData,
	) {

		combineLatest([
			this.cmmsService.getTriggerTypes$().pipe(distinctUntilChanged()),
			this.siteSubscriptions.selectedAccountGUID$.pipe(distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(distinctUntilChanged())
		]).pipe(
			takeUntil(this.finalise),
			filter(([TriggerTypes, selectedAccountGUID, selectedOrganisationUID, currentUser]) =>
				!!TriggerTypes && !!selectedAccountGUID && !!selectedOrganisationUID && !!currentUser
			)
		).subscribe({
			next: ([TriggerTypes, selectedAccountGUID, selectedOrganisationUID, currentUser]) => {

				this.triggerTypes = TriggerTypes;

				this.selectedAccountUID = selectedAccountGUID;
				this.selectedOrganisationUID = selectedOrganisationUID;
				this.currentUser = currentUser;

				if (this.dialogData.assetItem) {
					this.asset = this.dialogData.assetItem;
					this.getRules(this.asset);
				}

				if (this.dialogData.parent) {
					this.parent = this.dialogData.parent;
				}

				if (!this.dialogData.editing) {
					this.dialogForm.patchValue({ UserID: this.currentUser.UserID });
				}

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: combined route parameters", false);
			}
		});
	}

	/**
	 * ngOnInit
	 * if not attached to event, set attachedToEvent to false, if editing, 
	 * patch form values with case file values and disable trigger type form control 
	 * since we don't want that to be editable as it is used for categorisation of cases 
	 * in the CMMS and changing it could cause issues with reporting and organisation of cases
	 */
	ngOnInit(): void {

		if (!this.dialogData.isAttachedtoEvent) {
			this.attachedToEvent = false;
		}

		if (this.dialogData.editing) {
			this.editing = true;
			this.caseFile = this.dialogData.caseFile;
			this.dialogForm = this.fb.group({
				Id: [this.caseFile.Id],
				TriggerType: [{ value: this.caseFile.TriggerType, disabled: true }, Validators.required],
				RuleList: [''],
				RuleJSON: [''],
				Description: [this.caseFile.Description, Validators.required],
				UserID: [this.caseFile.UserID],
				skipCreate: [false],
			});
		} else {
			this.caseFile = this.dialogData.caseFile;
			let TriggerType =
			{
				value: this.caseFile.TriggerType,
				disabled: (this.caseFile.TriggerType ? true : false) || this.attachedToEvent
			}

			let RuleList =
			{
				value: '',
				disabled: false // this.caseFile.TriggerType == 7 ? true : false
			}

			this.dialogForm = this.fb.group({
				Id: [''],
				TriggerType: [TriggerType, Validators.required],
				RuleList: [RuleList],
				RuleJSON: [''],
				Description: ['', Validators.required],
				UserID: [0],
				skipCreate: [false],
			});
		}
	}

	/**
	 * getTriggerTypeDesc
	 * @param trigger 
	 * @returns 
	 */
	getTriggerTypeDesc(trigger: number): string {
		return this.triggerTypes.find(t => t.TriggerType == trigger)?.Description ?? "Unknown"
	}
	/**
	 * onCancelClick
	 * if the form is dirty, 
	 * confirm with the user before closing the dialog and losing changes
	 */
	onCancelClick(): void {
		this.dialogForm.patchValue({ skipCreate: true });
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	onSkipClick(): void {
		this.dialogForm.patchValue({ skipCreate: true });
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(this.dialogForm.value); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close(this.dialogForm.value);
		}
	}

	/**
	 * getCommentLength
	 * gets the length of the comment in the description form control, 
	 * used for displaying the character count hint
	 * @returns 
	 */
	getCommentLength(): number {
		return this.dialogForm.get('Description')?.value.length || 0;
	}

	/**
	 * getRules
	 * gets rules for the asset and populates the Rules property, 
	 * which is used to display rules in the multi-select dropdown. 
	 * If there are no rules, the dropdown is hidden.
	 * @param asset 
	 */
	async getRules(asset: LevelAsset) {

		try {
			const rules = await firstValueFrom(
				this.rulesDataService.getRules$(this.selectedAccountUID, this.selectedOrganisationUID, asset.AssetID).pipe(
					filter((asset) => !!asset)
				)
			);
			if (rules?.length) {
				if (rules[0].RuleID != 0) {
					this.Rules = rules;
				}
			}
		}
		catch (error: any) {
			if (error instanceof HttpErrorResponse && error.status === 404) { return; }
			this.globalFunctions.HandleError(error, "Error: this.rulesDataService.getRules$", false);
		}

	}

	/**
	 * onDialogClose	 
	 * if the form is valid and dirty, confirm with the user that they want to create the case with the selected trigger type, 
	 * if they confirm, patch the form value with the user ID (if not already patched) 
	 * and selected rules (if any) and close the dialog passing the form value back to the parent component which called the dialog. 
	 * If they do not confirm, do nothing and keep the dialog open.
	 * @returns 
	 */
	async onDialogClose() {
		if (this.dialogForm.valid && this.dialogForm.dirty) {

			var triggerType = 0
			if (this.dialogData.creating && this.dialogData.isAttachedtoEvent && this.dialogData.caseFile) {
				triggerType = this.dialogData.caseFile.TriggerType;
			} else {
				triggerType = this.dialogForm.value['TriggerType'];
			}

			var triggerDescription = this.triggerTypes.find(x => x.TriggerType == triggerType)?.Description || "Unknown";
			const okResult = await firstValueFrom(this.globalFunctions.UserConfirm$('Create CMMS Case for ' + triggerDescription + '?'));

			if (!okResult) {
				return; // 🚨 properly stops execution now
			}

			if (!this.dialogData.editing) { this.dialogForm.patchValue({ UserID: this.currentUser.UserID }); }

			if (this.dialogForm.value['RuleList']) {
				let selectedRules = this.Rules.filter((x: Rule) => this.dialogForm.value['RuleList'].includes(x.RuleID));
				if (selectedRules.length > 0) { this.dialogForm.patchValue({ RuleJSON: JSON.stringify(selectedRules) }); }
			}

			this.dialogForm.patchValue({ skipCreate: false });
			this.dialogForm.updateValueAndValidity();
			this.dialogRef.close(this.dialogForm.value);
		}
	}

	/**
	 * onSelectionChange
	 * if select all is selected, add all rule IDs to the form control value
	 * @param event 
	 */
	onSelectionChange(event: any) {
		const value = event.value;

		if (value.includes('__all__')) {
			// Select everything
			const allIds = this.Rules.map(r => r.RuleID);
			this.dialogForm.get('RuleList')?.setValue(allIds);
		}
	}

}
